import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import uniqBy from "lodash/uniqBy";
import {
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_PARENT_ACTIVITY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useBookingCategoryLink } from "PFApp/hooks/activity_form/use_booking_category_link";
import { useGetCustomFieldsToLink } from "PFApp/hooks/activity_form/use_get_custom_fields_to_link";
import { usePropertyFieldValueChange } from "PFApp/hooks/activity_form/use_property_field_value_change";
import { useSubtemplateDefaultValues } from "PFApp/hooks/activity_form/use_subtemplate_default_values";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import { useSubtemplatesDefaultAPICustomValues } from "PFCore/hooks/queries/subtemplates/use_subtemplates_default_api_custom_values";
import { useFormContext, useWatch } from "react-hook-form";

import { useActivityFormStore } from "../activity_form_store";
import { useEngagementTemplate } from "./use_engagement_template";
import { useSubtemplate } from "./use_subtemplate";

type PopulateFromAllSourcesAttributes = {
  sourceValues: FormFieldData[];
  source: "engagement" | "subtemplate";
};

export const useFieldsAutoPopulation = () => {
  const template = useActivityFormStore((state) => state.template);
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const subtemplate = useSubtemplate();
  const parentTemplate = useEngagementTemplate();
  const getCustomFieldsToLink = useGetCustomFieldsToLink({ template, parentTemplate });
  const changePropertyFieldValue = usePropertyFieldValueChange({
    template,
    subtemplate,
    activityCreator
  });
  const { getSubtemplateDefaultValues } = useSubtemplateDefaultValues();
  const { getAPICustomValuesByText } = useSubtemplatesDefaultAPICustomValues({
    templateKey: template.key
  });
  const { setValue } = useFormContext();
  const selectedParentActivity = useWatch({ name: FIELD_PARENT_ACTIVITY_KEY });
  const bookingCategoryId = useWatch({ name: FIELD_BOOKING_CATEGORY_ID_KEY });
  const { getBookingCategoryLinkHelpers } = useBookingCategoryLink({ template });

  const populateFromAllSources = ({ sourceValues, source }: PopulateFromAllSourcesAttributes) => {
    let engagementDataToPopulate: FormFieldData[] = [];
    if (source === "engagement") {
      engagementDataToPopulate = sourceValues;
    } else if (selectedParentActivity) {
      const customFieldsToPopulate = getCustomFieldsToLink({
        engagement: selectedParentActivity,
        roleSubtemplate: subtemplate
      }).map<FormFieldData>(({ type, values }) => ({
        values,
        fieldName: type.name
      }));

      const { canLink: canLinkBookingCategory, parentBookingCategoryId } = getBookingCategoryLinkHelpers({
        parentActivity: selectedParentActivity,
        activityCreator,
        subtemplate,
        bookingCategoryId
      });

      engagementDataToPopulate = compact([
        ...customFieldsToPopulate,
        canLinkBookingCategory && {
          fieldName: "booking_category_id",
          values: parentBookingCategoryId!
        }
      ]);
    }

    const { metadataFields, customFields } = subtemplate
      ? getSubtemplateDefaultValues({
          subtemplate,
          activityCreator
        })
      : { metadataFields: [], customFields: [] };

    const customFieldsWithApiValues = compact(
      customFields.map(({ fieldName, values }) => ({
        fieldName,
        values: getAPICustomValuesByText(fieldName, values)
      }))
    ) as FormFieldData[];

    const subtemplateDataToPopulate: FormFieldData[] =
      source === "subtemplate" ? sourceValues : [...metadataFields, ...customFieldsWithApiValues];

    // Source importance: Engagement > Subtemplate
    const mergedCustomValues = cloneDeep(
      uniqBy(
        [
          ...engagementDataToPopulate.map((data) => ({ ...data, bypassReadPermission: true })),
          ...subtemplateDataToPopulate.map((data) => ({ ...data, bypassReadPermission: true }))
        ],
        (data) => data.fieldName
      )
    );

    mergedCustomValues.forEach(({ fieldName, values, bypassReadPermission }) => {
      if (fieldName === "booking_category_id") {
        setValue(FIELD_BOOKING_CATEGORY_ID_KEY, values);
        return;
      }
      changePropertyFieldValue(fieldName, values, bypassReadPermission);
    });
  };

  return {
    populateFromAllSources
  };
};
